import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { api } from '@/lib/api'
import type { JobApplication, PageMeta, Vacancy } from '@/lib/types'

export interface VacancyFilters {
  q?: string
  category?: string
  employment_type?: string
  work_arrangement?: string
  experience_level?: string
  city?: string
  min_salary?: number
  page?: number
}

interface VacancyList {
  vacancies: Vacancy[]
  meta: PageMeta
}

export function useVacancies(filters: VacancyFilters) {
  return useQuery({
    queryKey: ['vacancies', filters],
    queryFn: () => api.get<VacancyList>('/vacancies', { query: { ...filters, per_page: 12 } }),
    staleTime: 60_000,
  })
}

export function useVacancy(vacancyId: string) {
  return useQuery({
    queryKey: ['vacancy', vacancyId],
    queryFn: () => api.get<{ vacancy: Vacancy; is_manager: boolean }>(`/vacancies/${vacancyId}`),
  })
}

export function useCompanyVacancies(companyId: string | undefined) {
  return useQuery({
    queryKey: ['company-vacancies', companyId],
    queryFn: () => api.get<VacancyList>(`/companies/${companyId}/vacancies`, { query: { per_page: 50 } }),
    enabled: Boolean(companyId),
  })
}

export interface VacancyInput {
  title: string
  description: string
  requirements: string
  category: string
  skills: string[]
  employment_type: string
  work_arrangement: string
  experience_level: string
  city: string
  salary_min: number
  salary_max: number
  salary_visible: boolean
  openings: number
}

export function useCreateVacancy(companyId: string) {
  const client = useQueryClient()

  return useMutation({
    mutationFn: (input: VacancyInput) => api.post<{ vacancy: Vacancy }>(`/companies/${companyId}/vacancies`, input),
    onSuccess: () => client.invalidateQueries({ queryKey: ['company-vacancies'] }),
  })
}

export function useUpdateVacancy(vacancyId: string) {
  const client = useQueryClient()

  return useMutation({
    mutationFn: (input: VacancyInput) => api.patch<{ vacancy: Vacancy }>(`/vacancies/${vacancyId}`, input),
    onSuccess: () => {
      client.invalidateQueries({ queryKey: ['vacancy', vacancyId] })
      client.invalidateQueries({ queryKey: ['company-vacancies'] })
    },
  })
}

function useVacancyMutation<TInput, TData>(vacancyId: string, mutationFn: (input: TInput) => Promise<TData>) {
  const client = useQueryClient()

  return useMutation({
    mutationFn,
    onSuccess: () => {
      client.invalidateQueries({ queryKey: ['vacancy', vacancyId] })
      client.invalidateQueries({ queryKey: ['company-vacancies'] })
      client.invalidateQueries({ queryKey: ['vacancies'] })
    },
  })
}

export function usePublishVacancy(vacancyId: string) {
  return useVacancyMutation<void, { vacancy: Vacancy }>(vacancyId, () =>
    api.post<{ vacancy: Vacancy }>(`/vacancies/${vacancyId}/publish`),
  )
}

export function useCloseVacancy(vacancyId: string) {
  return useVacancyMutation<boolean, { vacancy: Vacancy }>(vacancyId, (filled: boolean) =>
    api.post<{ vacancy: Vacancy }>(`/vacancies/${vacancyId}/close`, { filled }),
  )
}

export function useVacancyReadiness(vacancyId: string, enabled = true) {
  return useQuery({
    queryKey: ['vacancy-readiness', vacancyId],
    queryFn: () => api.get<{ ready: boolean; reasons: string[] }>(`/vacancies/${vacancyId}/readiness`),
    enabled,
  })
}

export interface ApplyInput {
  cover_letter: string
  expected_salary?: number
  resume_url?: string
}

export function useApply(vacancyId: string) {
  const client = useQueryClient()

  return useMutation({
    mutationFn: (input: ApplyInput) =>
      api.post<{ application: JobApplication }>(`/vacancies/${vacancyId}/apply`, input),
    onSuccess: () => {
      client.invalidateQueries({ queryKey: ['vacancy', vacancyId] })
      client.invalidateQueries({ queryKey: ['my-applications'] })
    },
  })
}

export function useMyApplications(state?: string) {
  return useQuery({
    queryKey: ['my-applications', state],
    queryFn: () => api.get<{ applications: JobApplication[]; meta: PageMeta }>('/me/applications', {
      query: { state, per_page: 50 },
    }),
  })
}

export function useApplication(applicationId: string | null) {
  return useQuery({
    queryKey: ['application', applicationId],
    queryFn: () => api.get<{ application: JobApplication }>(`/applications/${applicationId}`),
    select: (data) => data.application,
    // Dialog rincian memakai hook ini sebelum ada lamaran yang dipilih;
    // tanpa penjaga ini permintaan terkirim ke jalur tanpa pengenal.
    enabled: Boolean(applicationId),
  })
}

export function useVacancyApplications(vacancyId: string, state?: string) {
  return useQuery({
    queryKey: ['vacancy-applications', vacancyId, state],
    queryFn: () =>
      api.get<{ applications: JobApplication[]; pipeline: Record<string, number>; meta: PageMeta }>(
        `/vacancies/${vacancyId}/applications`,
        { query: { state, per_page: 50 } },
      ),
  })
}

export function useAdvanceApplication(vacancyId: string) {
  const client = useQueryClient()

  return useMutation({
    mutationFn: (input: { applicationId: string; state: string; note?: string }) =>
      api.post<{ application: JobApplication }>(`/applications/${input.applicationId}/advance`, {
        state: input.state,
        note: input.note ?? '',
      }),
    onSuccess: () => {
      client.invalidateQueries({ queryKey: ['vacancy-applications', vacancyId] })
      client.invalidateQueries({ queryKey: ['application'] })
    },
  })
}

export function useWithdrawApplication() {
  const client = useQueryClient()

  return useMutation({
    mutationFn: (applicationId: string) =>
      api.post<{ application: JobApplication }>(`/applications/${applicationId}/withdraw`),
    onSuccess: () => client.invalidateQueries({ queryKey: ['my-applications'] }),
  })
}

export function useSaveVacancy() {
  const client = useQueryClient()

  return useMutation({
    mutationFn: (input: { vacancyId: string; saved: boolean }) =>
      input.saved
        ? api.delete<void>(`/vacancies/${input.vacancyId}/save`)
        : api.post<void>(`/vacancies/${input.vacancyId}/save`),
    onSuccess: (_data, input) => {
      client.invalidateQueries({ queryKey: ['vacancy', input.vacancyId] })
      client.invalidateQueries({ queryKey: ['saved-vacancies'] })
    },
  })
}

export function useSavedVacancies() {
  return useQuery({
    queryKey: ['saved-vacancies'],
    queryFn: () => api.get<VacancyList>('/me/saved-vacancies', { query: { per_page: 50 } }),
  })
}

/**
 * salaryLabel memilih sebutan yang benar untuk nominal pada sebuah lowongan.
 *
 * Kolomnya satu — salary_min dan salary_max — tetapi artinya berbeda menurut
 * jenis hubungan kerjanya. Menuliskan "gaji per bulan" pada pekerjaan lepas
 * yang dibayar per proyek bukan sekadar tidak tepat: pelamar akan menghitung
 * penghasilan bulanan dari angka yang sebenarnya berlaku sekali per proyek.
 */
export function salaryLabel(employmentType: string): string {
  switch (employmentType) {
    case 'freelance':
      return 'Imbalan per proyek'
    case 'contract':
      return 'Nilai kontrak'
    default:
      return 'Gaji per bulan'
  }
}
